import { Dialog, Transition } from '@headlessui/react'
import type { PropsWithChildren, ReactNode } from 'react'
import { useRef, Fragment } from 'react'
import CloseIcon from '~/components/icons/Close_v2'
import Divider from '~/components/shared/Divider'
import { classNames } from '~/helpers/string'

type ModalSize = 'sm' | 'md' | 'lg'

export type ModalProps = PropsWithChildren<{
  open: boolean
  onClose: () => void
  title: string
  description?: string | ReactNode
  size?: ModalSize
  initialFocus?: boolean
  dialogPanelClassName?: string
  dialogTitleClassName?: string
  dialogDividerClassName?: string
  childrenWrapperClassName?: string
  rootClassName?: string
}>

const sizeMap: Record<ModalSize, string> = {
  sm: 'max-w-[480px]',
  md: '',
  lg: 'max-w-[800px]',
}

function Modal(props: ModalProps) {
  const {
    open,
    onClose,
    title,
    description,
    children,
    initialFocus = true,
    size = 'md',
    dialogPanelClassName = '',
    dialogTitleClassName = '',
    dialogDividerClassName = '',
    childrenWrapperClassName = '',
    rootClassName = '',
  } = props

  // Workaround: to disable initial focus on dialog render
  const initialFocusRef = useRef(null)

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        initialFocus={initialFocus ? undefined : initialFocusRef}
        open={open}
        onClose={() => void 0}
        className="relative z-[100]"
      >
        <div style={{ display: 'none' }} ref={initialFocusRef} />
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>
        <div
          className={classNames(
            'fixed inset-0 w-screen overflow-hidden  pt-16 md:pb-16 xl:pb-8 xl:pt-[96px]',
            rootClassName,
          )}
        >
          <div className={classNames(`mx-auto flex h-full max-w-screen-lg items-start justify-center`, sizeMap[size])}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={classNames('flex max-h-full flex-col rounded-xl bg-white', dialogPanelClassName)}
              >
                <div className="flex-shrink-0">
                  <Dialog.Title
                    as="h2"
                    className={classNames('flex justify-between gap-10 p-8 pb-2.5', dialogTitleClassName)}
                  >
                    <p className="text-xl font-medium text-base-500">{title}</p>
                    <div className="h-7.5 w-7.5 cursor-pointer rounded-md bg-[#F9F9F9] p-[5px]" onClick={onClose}>
                      <CloseIcon className="text-[#707070]" width="20" height="20" />
                    </div>
                  </Dialog.Title>
                  <Divider className={classNames('mx-8', dialogDividerClassName)} />
                  {description && (
                    <Dialog.Description className="mb-[10px] mt-5 px-8 text-sm font-medium text-base-500">
                      {description}
                    </Dialog.Description>
                  )}
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden sm:rounded-b-xl">
                  <div
                    className={classNames(
                      'flex-1 overflow-y-auto overflow-x-hidden p-5 sm:p-8',
                      childrenWrapperClassName,
                    )}
                  >
                    {children}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default Modal
